import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import type { components } from '@sentinel/api-client';
import { isLaunchableUrl, productHost } from '../launch-url';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

type AuthProduct = components['schemas']['AuthProductResponse'];

const styles = stylex.create({
  card: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    paddingTop: '0.875rem',
    paddingBottom: '0.875rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    borderRadius: '0.5rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 60%, transparent)`,
    color: 'inherit',
    textDecoration: 'none',
  },
  cardDisabled: { opacity: 0.6 },
  body: { display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.5rem' },
  name: { fontWeight: 600 },
  host: {
    fontFamily: sentinel.fontMono,
    fontSize: '0.8125rem',
    // 0.72 (not the ApiKeyList prefix's 0.47) clears WCAG AA 4.5:1 against the
    // dark pill/card on the a11y-scanned /select-product screen.
    color: `color-mix(in oklab, ${sentinel.surfaceInverse} 72%, transparent)`,
    paddingTop: '0.125rem',
    paddingBottom: '0.125rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    borderRadius: '0.25rem',
    backgroundColor: sentinel.surfaceSunken,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
  },
  meta: { fontSize: '0.8125rem', color: sentinel.textMuted },
  glyph: { fontSize: '1rem', color: sentinel.textMuted },
});

export interface ProductCardProps {
  product: AuthProduct;
}

export function ProductCard({ product }: ProductCardProps) {
  const { t } = useTranslation('products');
  const testId = `product-card-${product.slug}`;

  if (isLaunchableUrl(product.portal_url)) {
    const url = product.portal_url!;
    return (
      <a {...stylex.props(styles.card)} href={url} data-testid={testId}>
        <span {...stylex.props(styles.body)}>
          <span {...stylex.props(styles.name)}>{product.name}</span>
          <span {...stylex.props(styles.host)}>{productHost(url)}</span>
        </span>
        <span {...stylex.props(styles.glyph)} aria-hidden="true">
          ↗
        </span>
      </a>
    );
  }

  return (
    <div {...stylex.props(styles.card, styles.cardDisabled)} aria-disabled="true" data-testid={testId}>
      <span {...stylex.props(styles.body)}>
        <span {...stylex.props(styles.name)}>{product.name}</span>
        <span {...stylex.props(styles.meta)}>{t('entry_not_configured')}</span>
      </span>
    </div>
  );
}
